import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Dropdown, Form, Grid, Label, Segment, Dimmer, Loader, Button, Input, Divider, Icon } from "semantic-ui-react";
import { Storage } from 'aws-amplify';
import { connect } from 'react-redux';
import _ from 'lodash';
import { useHistory } from "react-router-dom";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import GraphHtmlRenderer from "./GraphHtmlRenderer";
import GraphImageRenderer from "./GraphImageRenderer";

// Helper function to truncate long text
const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

// Helper function to get question text by ID
const getQuestionTextById = (questionId, analysisQuestions) => {
    if (!analysisQuestions || !Array.isArray(analysisQuestions)) {
        console.warn('No analysis questions provided for ID:', questionId);
        return `Question ${questionId}`;
    }

    const question = analysisQuestions.find(q => q.id === questionId);
    if (question && question.question) {
        return question.question;
    } else {
        console.warn('Question not found for ID:', questionId, 'Available questions:', analysisQuestions.map(q => q.id));
        return `Question ${questionId}`;
    }
};

// Helper function to parse filename and get question texts
const parseFilenameToQuestionTexts = (filename, selectedCustomQuery) => {
    try {
        // Remove .html or .png extension if present
        const nameWithoutExtension = filename.replace(/\.(html|png)$/, '');

        console.log('Parsing filename:', nameWithoutExtension);

        // Check if filename contains underscore (indicating two questions)
        if (nameWithoutExtension.includes('_')) {
            const parts = nameWithoutExtension.split('_');
            console.log('Split parts:', parts);

            if (parts.length >= 2) {
                const questionId1 = parts[0];
                const questionId2 = parts[1];

                console.log('Question IDs:', { questionId1, questionId2 });

                if (selectedCustomQuery && selectedCustomQuery.analysisQuestions) {
                    const questionText1 = getQuestionTextById(questionId1, selectedCustomQuery.analysisQuestions);
                    const questionText2 = getQuestionTextById(questionId2, selectedCustomQuery.analysisQuestions);

                    const result = `${questionText1} vs ${questionText2}`;
                    console.log('Parsed result:', result);
                    return result;
                }
            }
        } else {
            // Single question
            console.log('Single question ID:', nameWithoutExtension);

            if (selectedCustomQuery && selectedCustomQuery.analysisQuestions) {
                const questionText = getQuestionTextById(nameWithoutExtension, selectedCustomQuery.analysisQuestions);
                console.log('Single question result:', questionText);
                return questionText;
            }
        }

        // Fallback to original filename if parsing fails
        console.log('Falling back to original filename:', filename);
        return filename;
    } catch (error) {
        console.error('Error parsing filename:', error);
        return filename;
    }
};

const CustomAnalysisDataPage = (props) => {
    const { tenant, selectedCustomQuery, } = props;
    const history = useHistory();
    const [state, setState] = useState({
        loading: false,
        isViewFileOpen: false,
        downloadingCsv: false,
        exportingPdf: false,
    });
    const [graphHtmls, setGraphHtmls] = useState([]);

    useEffect(() => {
        const plotlyScriptSrc = "https://cdn.plot.ly/plotly-3.0.1.min.js";
        const alreadyLoaded = !!document.querySelector(`script[src="${plotlyScriptSrc}"]`);
        if (!alreadyLoaded) {
            const script = document.createElement("script");
            script.src = plotlyScriptSrc;
            script.async = false;
            // script.onload = () => console.log("Plotly loaded");
            document.body.appendChild(script);
        }
    });

    useEffect(() => {
        if (selectedCustomQuery) {
            setState(s => ({ ...s, loading: true }));
            // Show confirmation that query was loaded
            toast.success(`Analysis query loaded: ${selectedCustomQuery.id.substring(0, 8)}...`);
            fetchQnaireGraphsFromS3();
        } else {
            setGraphHtmls([]);
            // If no query is selected, show a message and provide navigation back
            toast.info('No analysis query selected. Please select a query from the Custom Analysis page.');
        }
    }, [selectedCustomQuery]);

    const fetchQnaireGraphsFromS3 = async () => {
        if (!_.isEmpty(selectedCustomQuery)) {
            try {
                const basePath = `${process.env.REACT_APP_ENV}/plots/${selectedCustomQuery.tenantId}/custom/${selectedCustomQuery.id}/`;
                // const basePath = `${process.env.REACT_APP_ENV}/plots/${selectedCustomQuery.tenantId}/custom/151e6ade0ee3475137f94bb80d6678edcbdf878a/`;

                const listResult = await Storage.list(basePath, { level: 'public' });

                // Check if this is a word cloud analysis
                const isWordCloud = selectedCustomQuery.textGraphType === 'wordCloud';
                const fileExtension = isWordCloud ? '.png' : '.html';

                const graphFiles = listResult
                    ? listResult.filter(file => file.key.endsWith(fileExtension))
                    : (listResult.filter ? listResult.filter(file => file.key.endsWith(fileExtension)) : []);

                if (!graphFiles.length) {
                    toast.warning(
                        <>
                            No data found for this custom analysis.
                            <br />
                            Please run the analysis.
                        </>
                    );
                    setGraphHtmls([]);
                    setState(s => ({ ...s, loading: false }));
                    return;
                }

                const graphFileData = await Promise.all(
                    graphFiles.map(async (file) => {
                        const url = await Storage.get(file.key);

                        if (isWordCloud) {
                            // For PNG files, we just need the URL
                            const match = file.key.match(/\/([^\/]+)\.png$/);
                            const filename = match ? match[1] : file.key;

                            // Parse filename to get meaningful question texts
                            const displayName = parseFilenameToQuestionTexts(filename, selectedCustomQuery);

                            return {
                                imageUrl: url,
                                name: displayName, // Use the parsed display name
                                originalFilename: filename, // Keep original filename for reference
                                lastModified: file.lastModified,
                                key: file.key,
                                isImage: true,
                            };
                        } else {
                            // For HTML files, fetch the content as before
                            const response = await fetch(url);
                            if (!response.ok) {
                                toast.error(`Failed to fetch file: ${file.key}`);
                                return null;
                            }
                            const html = await response.text();
                            const match = file.key.match(/\/([^\/]+)\.html$/);
                            const filename = match ? match[1] : file.key;

                            // Parse filename to get meaningful question texts
                            const displayName = parseFilenameToQuestionTexts(filename, selectedCustomQuery);

                            return {
                                html,
                                name: displayName, // Use the parsed display name
                                originalFilename: filename, // Keep original filename for reference
                                lastModified: file.lastModified,
                                key: file.key,
                                isImage: false,
                            };
                        }
                    })
                );
                setGraphHtmls(graphFileData.filter(Boolean));
                setState(s => ({ ...s, loading: false }));
            } catch (error) {
                setGraphHtmls([]);
                setState(s => ({ ...s, loading: false }));
                console.error("Error fetching graph files from S3:", error.message);
            }
        }
    };

    const downloadCsvFromS3 = async () => {
        if (!selectedCustomQuery) {
            toast.error("Please select a custom analysis query first.");
            return;
        }

        setState(s => ({ ...s, downloadingCsv: true }));

        try {
            const basePath = `${process.env.REACT_APP_ENV}/plots/${selectedCustomQuery.tenantId}/${selectedCustomQuery.id}/datasets/all_answers.csv`;
            const listResult = await Storage.list(basePath, { level: 'public' });

            // Look for CSV files in the data folder
            const csvFiles = listResult
                ? listResult.filter(file => file.key.endsWith('.csv'))
                : (listResult.filter ? listResult.filter(file => file.key.endsWith('.csv')) : []);

            if (!csvFiles.length) {
                toast.warning("No CSV data files found for this questionnaire. Please run the analysis first.");
                setState(s => ({ ...s, downloadingCsv: false }));
                return;
            }

            // If multiple CSV files exist, download the most recent one or all of them
            if (csvFiles.length === 1) {
                // Download single CSV file
                const csvFile = csvFiles[0];
                const url = await Storage.get(csvFile.key);

                // Create a temporary link to download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = `custom_analysis_${selectedCustomQuery.id}_data.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.success("CSV file downloaded successfully!");
            } else {
                // Multiple CSV files - download the most recent one
                const mostRecentCsv = csvFiles.reduce((latest, current) => {
                    return (!latest.lastModified || current.lastModified > latest.lastModified) ? current : latest;
                });

                const url = await Storage.get(mostRecentCsv.key);

                // Create a temporary link to download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = `custom_analysis_${selectedCustomQuery.id}_data.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                toast.success("Most recent CSV file downloaded successfully!");
            }

        } catch (error) {
            console.error("Error downloading CSV from S3:", error);
            toast.error("Failed to download CSV file. Please try again.");
        } finally {
            setState(s => ({ ...s, downloadingCsv: false }));
        }
    };

    const exportGraphsAsPDF = async () => {
        if (graphHtmls.length === 0) {
            toast.error("No graphs available to export.");
            return;
        }

        setState(s => ({ ...s, exportingPdf: true }));
        toast.info("Generating PDF... This may take a moment.");

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const spacing = 5;
            const graphWidth = (pageWidth - 2 * margin - spacing) / 2;
            let currentY = margin;
            let currentX = margin;
            let graphsInRow = 0;
            let maxRowHeight = 0;

            for (let i = 0; i < graphHtmls.length; i++) {
                const graphContainer = document.getElementById(`graph-container-${i}`);
                if (graphContainer) {
                    const canvas = await html2canvas(graphContainer, { scale: 1 });
                    const imgData = canvas.toDataURL('image/jpeg', 0.7);
                    let imgHeight = (canvas.height * graphWidth) / canvas.width;
                    
                    if (imgHeight > 70) {
                        imgHeight = 70;
                    }

                    if (currentY + imgHeight > pageHeight - margin) {
                        pdf.addPage();
                        currentY = margin;
                        currentX = margin;
                        graphsInRow = 0;
                        maxRowHeight = 0;
                    }

                    pdf.addImage(imgData, 'JPEG', currentX, currentY, graphWidth, imgHeight);
                    maxRowHeight = Math.max(maxRowHeight, imgHeight);
                    graphsInRow++;

                    if (graphsInRow >= 2) {
                        currentY += maxRowHeight + spacing;
                        currentX = margin;
                        graphsInRow = 0;
                        maxRowHeight = 0;
                    } else {
                        currentX += graphWidth + spacing;
                    }
                }
            }

            pdf.save(`custom_analysis_${Date.now()}.pdf`);
            toast.success("PDF exported successfully!");
        } catch (error) {
            console.error("Error exporting PDF:", error);
            toast.error("Failed to export PDF. Please try again.");
        } finally {
            setState(s => ({ ...s, exportingPdf: false }));
        }
    };

    const refreshQnaireData = () => {
        setGraphHtmls([]);
        toast.info(`Checking for questionnaire analysis data...`);
        setState({
            ...state,
            loading: true,
        });
        fetchQnaireGraphsFromS3();
    };

    return (
        <Segment style={{ minHeight: 120, position: 'relative', zIndex: 0 }}>
            <Grid stackable columns={2}>
                <Grid.Row>
                    <Grid.Column width={12}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Button
                                icon="arrow left"
                                color="grey"
                                size="small"
                                onClick={() => {
                                    history.push('/General Actions/Deep Analysis?tab=custom');
                                }}
                                title="Back to Custom Analysis"
                            />
                            <div>
                                <h3 style={{ margin: 0 }}>Custom Analysis Data</h3>
                                {graphHtmls.length > 0 && (
                                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                        {graphHtmls.filter(file => file.name !== file.originalFilename).length} of {graphHtmls.length} graphs:
                                    </div>
                                )}
                            </div>
                        </div>
                    </Grid.Column>
                    <Grid.Column floated="right" width={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Form.Button compact primary icon='refresh' size='small' floated='right'
                            onClick={(e) => {
                                e.preventDefault();
                                refreshQnaireData();
                            }}></Form.Button>
                    </Grid.Column>
                    <Grid.Column floated="right" width={4} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                        <Form.Button
                            compact
                            primary
                            size='small'
                            loading={state.downloadingCsv}
                            disabled={selectedCustomQuery === null || state.downloadingCsv}
                            onClick={(e) => {
                                e.preventDefault();
                                downloadCsvFromS3();
                            }}
                            title="Download CSV Data"
                        >Download CSV</Form.Button>
                        <Form.Button
                            compact
                            color='green'
                            size='small'
                            loading={state.exportingPdf}
                            disabled={graphHtmls.length === 0 || state.exportingPdf}
                            onClick={(e) => {
                                e.preventDefault();
                                exportGraphsAsPDF();
                            }}
                            title="Export Graphs as PDF"
                        >Export PDF</Form.Button>
                    </Grid.Column>
                </Grid.Row>
                {/* {selectedCustomQuery && (
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <div style={{
                                padding: '12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                marginBottom: '16px'
                            }}>
                                <h4 style={{ marginBottom: '12px', color: '#495057' }}>Selected Query Details</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '14px' }}>
                                    <div>
                                        <strong>Query ID:</strong>
                                        <span style={{
                                            fontFamily: 'monospace',
                                            backgroundColor: '#e9ecef',
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            marginLeft: '8px'
                                        }}>
                                            {selectedCustomQuery.id}
                                        </span>
                                    </div>
                                    {selectedCustomQuery.userGroup && (
                                        <div>
                                            <strong>User Group:</strong>
                                            <span style={{ marginLeft: '8px' }}>
                                                {selectedCustomQuery.userGroup.name || selectedCustomQuery.userGroup.id}
                                            </span>
                                        </div>
                                    )}
                                    {selectedCustomQuery.kmlName && (
                                        <div>
                                            <strong>KML File:</strong>
                                            <span style={{ marginLeft: '8px' }}>{selectedCustomQuery.kmlName}</span>
                                        </div>
                                    )}
                                    <div>
                                        <strong>Analysis Questions:</strong>
                                        <span style={{ marginLeft: '8px' }}>
                                            {selectedCustomQuery.analysisQuestions ? selectedCustomQuery.analysisQuestions.length : 0}
                                        </span>
                                    </div>
                                    <div>
                                        <strong>Filter Questions:</strong>
                                        <span style={{ marginLeft: '8px' }}>
                                            {selectedCustomQuery.filterQuestions ? selectedCustomQuery.filterQuestions.length : 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Grid.Column>
                    </Grid.Row>
                )} */}
                <Grid.Row>
                    {state.loading ? (
                        <Grid.Column width={16} style={{ minHeight: 80, position: 'relative', zIndex: 0 }}>
                            <Dimmer active inverted style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1 }}>
                                <Loader>Loading...</Loader>
                            </Dimmer>
                        </Grid.Column>
                    ) : selectedCustomQuery == null ? (
                        <Grid.Column width={16} style={{ minHeight: 80 }}>
                            <div style={{
                                color: '#888',
                                padding: '2em 0',
                                textAlign: 'center',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <Icon name="search" size="large" style={{ marginBottom: '16px', color: '#6c757d' }} />
                                <div style={{ fontSize: '16px', marginBottom: '12px' }}>
                                    No custom analysis query selected
                                </div>
                                <div style={{ fontSize: '14px', marginBottom: '20px', color: '#6c757d' }}>
                                    Please select a query from the Custom Analysis page to view its data.
                                </div>
                                <Button
                                    primary
                                    icon="arrow left"
                                    content="Go to Custom Analysis"
                                    onClick={() => {
                                        history.push('/General Actions/Deep Analysis?tab=custom');
                                    }}
                                />
                            </div>
                        </Grid.Column>
                    ) : graphHtmls.length === 0 ? (
                        <Grid.Column width={16} style={{ minHeight: 80 }}>
                            <div style={{ color: '#888', padding: '1em 0' }}>
                                The questionnaire still needs to be analysed.
                            </div>
                        </Grid.Column>
                    ) : (
                        (() => {
                            // Sort graphHtmls to show graphs where both questions are yes_no first
                            const sortedGraphHtmls = [...graphHtmls].sort((a, b) => {
                                // Parse originalFilename to extract question IDs for both files
                                const parseQuestionIds = (filename) => {
                                    if (filename && filename.includes('_')) {
                                        const parts = filename.split('_');
                                        return {
                                            questionId1: parts[0],
                                            questionId2: parts[1],
                                            isTwoQuestions: true
                                        };
                                    } else {
                                        return {
                                            questionId1: filename,
                                            questionId2: null,
                                            isTwoQuestions: false
                                        };
                                    }
                                };

                                const { questionId1: questionId1A, questionId2: questionId2A, isTwoQuestions: isTwoQuestionsA } = parseQuestionIds(a.originalFilename);
                                const { questionId1: questionId1B, questionId2: questionId2B, isTwoQuestions: isTwoQuestionsB } = parseQuestionIds(b.originalFilename);

                                // Find the questions in analysisQuestions
                                const question1A = selectedCustomQuery.analysisQuestions.find(q => q.id === questionId1A);
                                const question2A = questionId2A ? selectedCustomQuery.analysisQuestions.find(q => q.id === questionId2A) : null;
                                const question1B = selectedCustomQuery.analysisQuestions.find(q => q.id === questionId1B);
                                const question2B = questionId2B ? selectedCustomQuery.analysisQuestions.find(q => q.id === questionId2B) : null;

                                // Check if both questions are yes_no for file A
                                const bothYesNoA = isTwoQuestionsA
                                    ? (question1A?.questionType === 'yes_no' && question2A?.questionType === 'yes_no')
                                    : (question1A?.questionType === 'yes_no'); // Single question case

                                // Check if both questions are yes_no for file B
                                const bothYesNoB = isTwoQuestionsB
                                    ? (question1B?.questionType === 'yes_no' && question2B?.questionType === 'yes_no')
                                    : (question1B?.questionType === 'yes_no'); // Single question case

                                // Graphs where both questions are yes_no come first
                                if (bothYesNoA && !bothYesNoB) return -1;
                                if (!bothYesNoA && bothYesNoB) return 1;

                                // Keep original order for same types
                                return 0;
                            });

                            const items = [];
                            let colIdx = 0;
                            for (let i = 0; i < sortedGraphHtmls.length; i++) {
                                const file = sortedGraphHtmls[i];

                                // Parse originalFilename to extract question IDs
                                const parseQuestionIds = (filename) => {
                                    if (filename && filename.includes('_')) {
                                        const parts = filename.split('_');
                                        return {
                                            questionId1: parts[0],
                                            questionId2: parts[1],
                                            isTwoQuestions: true
                                        };
                                    } else {
                                        return {
                                            questionId1: filename,
                                            questionId2: null,
                                            isTwoQuestions: false
                                        };
                                    }
                                };

                                const { questionId1, questionId2, isTwoQuestions } = parseQuestionIds(file.originalFilename);

                                // Find the questions in analysisQuestions
                                const question1 = selectedCustomQuery.analysisQuestions.find(q => q.id === questionId1);
                                const question2 = questionId2 ? selectedCustomQuery.analysisQuestions.find(q => q.id === questionId2) : null;

                                // Check if either question is location type
                                const isLocation = (question1 && (question1.questionType === 'location' || question1.questionType === 'mc_sel_one' || question1.questionType === 'multi_select')) ||
                                    (question2 && (question2.questionType === 'location' || question2.questionType === 'mc_sel_one' || question2.questionType === 'multi_select'));

                                if (isLocation) {
                                    items.push(
                                        <Grid.Column width={16} key={"graph-location-" + i} style={{ marginBottom: 24 }}>
                                            <div id={`graph-container-${i}`} style={{ border: '5px solid #eee', borderRadius: 6, padding: 8, background: '#fafafa', minHeight: 200, overflow: 'auto' }}>
                                                <Label style={{ marginBottom: '8px', display: 'block' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {file.name}
                                                    </div>
                                                </Label>
                                                <div style={{ border: '1px solid #eee', borderRadius: 6, background: '#fafafa', minHeight: 200, overflow: 'auto' }}>
                                                    {file.isImage ? (
                                                        <GraphImageRenderer imageUrl={file.imageUrl} idx={i} name={file.name} />
                                                    ) : (
                                                        <GraphHtmlRenderer html={file.html} idx={i} />
                                                    )}
                                                </div>
                                                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Label>
                                                        Last Modified: {file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}
                                                    </Label>
                                                </div>
                                            </div>
                                        </Grid.Column>
                                    );
                                    colIdx = 0;
                                } else {
                                    items.push(
                                        <Grid.Column width={8} key={"graph-" + i} style={{ marginBottom: 24, display: 'flex', flexDirection: 'column' }}>
                                            <div id={`graph-container-${i}`} style={{ border: '5px solid #eee', borderRadius: 6, padding: 8, background: '#fafafa', minHeight: 200, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                <Label style={{ marginBottom: '8px', display: 'block', fontWeight: 'bold' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {file.name} {/* This is now the parsed question text */}
                                                    </div>
                                                </Label>
                                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                                    {file.isImage ? (
                                                        <GraphImageRenderer imageUrl={file.imageUrl} idx={i} name={file.name} />
                                                    ) : (
                                                        <GraphHtmlRenderer html={file.html} idx={i} />
                                                    )}
                                                </div>
                                                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Label>
                                                        Last Modified: {file.lastModified ? new Date(file.lastModified).toLocaleString() : ''}
                                                    </Label>
                                                </div>
                                            </div>
                                        </Grid.Column>
                                    );
                                    colIdx++;
                                    if (colIdx === 2) colIdx = 0;
                                }
                            }
                            return items;
                        })()
                    )}
                </Grid.Row>
            </Grid>
        </Segment>
    )
}

const mapStateToProps = (state) => {
    return {
        tenant: state.tenant,
        selectedCustomQuery: state.selectedCustomQuery,
    };
}

export default connect(
    mapStateToProps,
    (dispatch) => ({
    })
)(CustomAnalysisDataPage);
